import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { gql, useQuery } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';

export const ENTERPRISE_FULFILLMENTS_QUERY = gql`
  query EnterpriseFulfillmentsQuery($businessEntityUuid: UUID!) {
    enterpriseFulfillments(businessEntityUuid: $businessEntityUuid) {
      jurisdictionCode
      status
      type
      uuid
    }
  }
`;

/**
 * @typedef {object} EnterpriseFulfillmentItem
 * @property {string} uuid - Unique fulfillment id (stable React key).
 * @property {string} type - Service type (e.g. llc_formation), aliased from orders-api `name`.
 * @property {string} status - Lowercase fulfillment status (e.g. incomplete, active).
 * @property {string} [jurisdictionCode] - State code, when present.
 */

/**
 * Fetches enterprise fulfillment status from orders-api (via the local BFF) and maps
 * each fulfillment to the shape the OrderTracker consumes. Single consumer, so a plain
 * hook rather than the OrderTrackerStatusProvider shared-fetch pattern.
 *
 * @returns {{
 *   error: object,
 *   items: EnterpriseFulfillmentItem[],
 *   loading: boolean
 * }}
 */
export function useEnterpriseFulfillments() {
  const { businessEntityUuid } = useBusinessEntity();

  const { data, error, loading } = useQuery(ENTERPRISE_FULFILLMENTS_QUERY, {
    noThrow: true,
    skip: !businessEntityUuid,
    variables: { businessEntityUuid }
  });

  useError(error);

  return {
    error,
    items: data?.enterpriseFulfillments ?? [],
    loading
  };
}
